import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import BlockIcon from '@mui/icons-material/Block';
import { useAuth } from '../../contexts/AuthContext';

interface DeviceApiKeysTabProps {
  deviceId: string;
}

export interface DeviceApiKey {
  id: string;
  deviceId: string;
  label: string;
  keySuffix: string | null;
  dateCreated: string;
  lastUsedDate: string | null;
  revokedDate: string | null;
}

interface KeyList {
  deviceId: string;
  // The site's business holds an active plan that includes the data API; keys are refused without one
  dataApiActive: boolean;
  keys: DeviceApiKey[];
}

interface CreatedKey extends DeviceApiKey {
  apiKey: string;
}

const formatDate = (value: string | null) => (value ? new Date(value).toLocaleString() : '—');

// A site's keys to the client data API (api/v1): each lets whoever holds it
// read this site's container and number-plate sightings, and nothing else.
const DeviceApiKeysTab: React.FC<DeviceApiKeysTabProps> = ({ deviceId }) => {
  const auth = useAuth();
  const accessToken = auth.tokens?.accessToken;
  const authHeader = { Authorization: `Bearer ${accessToken}` };

  const [list, setList] = useState<KeyList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [label, setLabel] = useState('');
  // The key and the row stay in state until their dialog has finished closing, so its text doesn't blank on the way out
  const [created, setCreated] = useState<CreatedKey | null>(null);
  const [createdOpen, setCreatedOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [revoking, setRevoking] = useState<DeviceApiKey | null>(null);
  const [revokeOpen, setRevokeOpen] = useState(false);

  const loadKeys = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`/api/devices/${deviceId}/api-keys`, { headers: authHeader });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error || 'Failed to load API keys');
      }
      setList(await res.json());
    } catch (err: any) {
      setError(err.message || 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, accessToken]);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  const createKey = async () => {
    try {
      setActionLoading(true);
      setError(null);
      const res = await fetch(`/api/devices/${deviceId}/api-keys`, {
        method: 'POST',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ label: label.trim() }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error || 'Failed to create API key');
      }
      setCreated(await res.json());
      setCreatedOpen(true);
      setCopied(false);
      setCreateOpen(false);
      setLabel('');
      await loadKeys();
    } catch (err: any) {
      setError(err.message || 'Failed to create API key');
      setCreateOpen(false);
    } finally {
      setActionLoading(false);
    }
  };

  const revokeKey = async () => {
    if (!revoking) return;
    try {
      setActionLoading(true);
      setError(null);
      const res = await fetch(`/api/devices/${deviceId}/api-keys/${revoking.id}`, {
        method: 'DELETE',
        headers: authHeader,
      });
      if (!res.ok) throw new Error('Failed to revoke API key');
      setRevokeOpen(false);
      await loadKeys();
    } catch (err: any) {
      setError(err.message || 'Failed to revoke API key');
      setRevokeOpen(false);
    } finally {
      setActionLoading(false);
    }
  };

  const copyKey = async () => {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.apiKey);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  if (loading && !list) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const keys = list?.keys ?? [];
  const exampleUrl = `${window.location.origin}/api/v1/containers`;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">API Keys</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
          disabled={!list?.dataApiActive}
        >
          New key
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        A key lets another system read this site's container and number-plate sightings, its cameras' included,
        through the data API. Give each party its own key, so one can be revoked without cutting off the others.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {list && !list.dataApiActive && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This site's business has no active subscription that includes the data API. New keys can't be issued,
          and the keys below are refused until it does.
        </Alert>
      )}

      {keys.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 2 }}>No API keys have been issued for this site.</Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Issued to</TableCell>
                <TableCell>Key</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Last used</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {keys.map((key) => (
                <TableRow key={key.id} sx={{ opacity: key.revokedDate ? 0.6 : 1 }}>
                  <TableCell>{key.label}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>dmo_dev_…{key.keySuffix}</Typography>
                  </TableCell>
                  <TableCell>{formatDate(key.dateCreated)}</TableCell>
                  <TableCell>
                    <Tooltip title="Kept to within a few minutes">
                      <span>{key.lastUsedDate ? formatDate(key.lastUsedDate) : 'Never'}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {key.revokedDate
                      ? <Chip size="small" label={`Revoked ${new Date(key.revokedDate).toLocaleDateString()}`} />
                      : <Chip size="small" color="success" label="Active" />}
                  </TableCell>
                  <TableCell align="right">
                    {!key.revokedDate && (
                      <Button size="small" color="error" startIcon={<BlockIcon />} onClick={() => { setRevoking(key); setRevokeOpen(true); }}>
                        Revoke
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>Using a key</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          The key goes in the <code>X-Api-Key</code> header. <code>/api/v1/containers</code> and <code>/api/v1/plates</code> return
          sightings oldest change first with a <code>nextCursor</code>; pass it back as <code>cursor</code> to get what's new or
          changed since. <code>/api/v1/site</code> gives the site, its cameras and what's at each dock now.
        </Typography>
        <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
          curl -H "X-Api-Key: dmo_dev_…" "{exampleUrl}?cursor=0"
        </Typography>
      </Paper>

      {/* New key */}
      <Dialog open={createOpen} onClose={() => !actionLoading && setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New API key</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Issued to"
            placeholder="e.g. the freight company's system"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            inputProps={{ maxLength: 255 }}
            helperText="Who the key is for, so you know which to revoke later"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={actionLoading}>Cancel</Button>
          <Button variant="contained" onClick={createKey} disabled={actionLoading || !label.trim()}>
            {actionLoading ? 'Creating…' : 'Create key'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* The key, once */}
      <Dialog
        open={createdOpen}
        onClose={() => setCreatedOpen(false)}
        TransitionProps={{ onExited: () => setCreated(null) }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Save this key now</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This key is shown once and can't be retrieved again. If it's lost, revoke it and issue another.
          </Alert>
          <Typography variant="caption" color="text.secondary">Issued to</Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>{created?.label}</Typography>
          <Typography variant="caption" color="text.secondary">API key</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', flexGrow: 1 }}>
              {created?.apiKey}
            </Typography>
            <Tooltip title={copied ? 'Copied' : 'Copy'}>
              <IconButton onClick={copyKey} size="small"><ContentCopyIcon fontSize="small" /></IconButton>
            </Tooltip>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setCreatedOpen(false)}>I've saved it</Button>
        </DialogActions>
      </Dialog>

      {/* Revoke */}
      <Dialog
        open={revokeOpen}
        onClose={() => !actionLoading && setRevokeOpen(false)}
        TransitionProps={{ onExited: () => setRevoking(null) }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Revoke this key?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            The key issued to <strong>{revoking?.label}</strong> (…{revoking?.keySuffix}) will stop working straight away,
            and can't be brought back.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeOpen(false)} disabled={actionLoading}>Cancel</Button>
          <Button color="error" variant="contained" onClick={revokeKey} disabled={actionLoading}>
            {actionLoading ? 'Revoking…' : 'Revoke key'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeviceApiKeysTab;
